// ClientFormBonDeComandaDetails.js
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { pdf } from "@react-pdf/renderer";
import { QRCodeCanvas } from "qrcode.react";

import {
  selectStyles,
  resolvePictureUrl,
  getNowLocalDate,
  getNowLocalTimeHHMM,
  buildSelectOptionsFromLocations,
  buildClientSignatureLink,
  formatRoDateTime
} from "../../util/common";

import * as StorageService from "../../services/StorageServices";
import { LOCALSTORAGE_ITEMS, DURATA_UNITS } from "../../util/Constants";
import defaultLogo from "../../assets/img/logo-mc-business.jpg";

import {
  createClientFormHandler,
  editClientFormByIdHandler,
  getClientFormByIdHandler,
  getUserLocationsHandler,
  getClockInOutHandler, 
} from "../../handlers/clientFormsHandlers/HandlerClientForms";

import {
  IconMail,
  IconMapPin,
  IconUser,
  IconCalendar,
  IconClock,
  IconClipboardList,
  IconPhone,
  IconPackage,
  IconFileText,
  IconWrench,
  IconBanknote,
  IconTimer,
  IconTriangleAlert,
} from "../../components/Icons";

import TimeInput24h from "../../components/TimeInput24h.js";
import Spinner from "../../components/Spinner";
import { makeSelectStylesGreenChecks, greenCheckComponents } from "../../util/selectGreenChecks";
import StatusBadge from "../../components/StatusBadge";
import IconBadge from "./components/IconBadge";
import FieldLabel from "./components/FieldLabel";
import ClientFormBonDeComandaPdfTemplate from "../../components/PdfTemplates/ClientFormBonDeComandaPdfTemplate";
import useWaitForClientSignature from "./hooks/useWaitForClientSignature.js";
import useDefaultLocationFromClockIn from "./hooks/useDefaultLocationFromClockIn";


function ClientFormBonDeComandaDetails(props) {
  console.log("ClientFormBonDeComandaDetails", props);

  const STATUS = {
    NEW: "NEW",
    PENDING: "PENDING",
    SIGNED: "SIGNED",
    CONFIRMED: "CONFIRMED",
  };

  const companyInfo = StorageService.getItem(LOCALSTORAGE_ITEMS.COMPANY_INFO)
    ? JSON.parse(StorageService.getItem(LOCALSTORAGE_ITEMS.COMPANY_INFO))
    : null;

  const firstName = StorageService.getItem(LOCALSTORAGE_ITEMS.FIRST_NAME) || "Unknown";
  const lastName = StorageService.getItem(LOCALSTORAGE_ITEMS.LAST_NAME) || "Unknown";
  const employeeId = StorageService.getItem(LOCALSTORAGE_ITEMS.USER_ID) || null;

  const companyId = companyInfo?.id ? String(companyInfo.id) : null;

  const [userLocations, setUserLocations] = useState([]);

  const FIELD_KEYS = {
    telefonMagazin: "telefonMagazin",
    email: "email",
    adresaPctLucru: "adresaPctLucru",
    logoUrl: "logoUrl",
    operator: "operator",
    data: "data",
    ora: "ora",
    client: "client",
    telefon: "telefon",
    produsReceptionat: "produsReceptionat",
    produsAltulText: "produsAltulText",
    stareProdusLaReceptie: "stareProdusLaReceptie",
    stareAltulText: "stareAltulText",
    operatiuneSolicitata: "operatiuneSolicitata",
    operatiuneAltulText: "operatiuneAltulText",
    estimareDurataValue: "estimareDurataValue",
    estimareDurataUnit: "estimareDurataUnit",
    pretEstimat: "pretEstimat",
    riscuriObservatii: "riscuriObservatii",
    acknowledged: "acknowledged",
    clientSignature: "clientSignature",

    clientConfirmSignature: "clientConfirmSignature",
    clientConfirmDate: "clientConfirmDate",

    receipt: "receipt",

    marca: "marca",
    model: "model",
    anFabricatie: "anFabricatie",
  };

  const FORM_ID_PREFIX = "BON_COMANDA";

  const getField = (fieldsObj, key, fallback = null) => {
    if (!fieldsObj) return fallback;
    const v = fieldsObj[key];
    return typeof v === "undefined" || v === null ? fallback : v;
  };

  const setFieldObj = (fieldsObj, key, value) => {
    fieldsObj[key] = value;
  };

  const RISKS_GROUPS = [
    {
      title: "Cheie auto cu telecomandă / Cheie auto valet",
      items: [
        "Posibilitatea afișării unor erori în bordul mașinii din cauza voltajului bateriei auto.",
        "Posibilitatea de deteriorare a plăcuței electronice și a transponderului în cazul telecomenzilor lipite.",
        "Posibilitatea de deteriorare suplimentară a carcaselor și cheilor uzate.",
        "Posibilă funcționare defectuoasă a telecomenzii în cazul cheilor cu plăci electronice uzate.",
        "Posibilitatea desincronizării telecomenzilor auto, din cauza lipsei voltajului constant pe plăcuță.",
        "Posibilitatea exfolierii circuitului electronic în cazul în care cipul sau carcasa sunt lipite și demontarea se realizează în mod specific.",
      ],
    },
    {
      title: "Telecomenzi rezidențiale",
      items: [
        "Posibilitatea ca anumite sisteme de acces rezidențiale sau comerciale să aibă restricții tehnice ori administrative privind programarea telecomenzilor suplimentare.",
        "Posibilitatea ca administratorul sistemului, producătorul automatizării sau firma de mentenanță să limiteze, blocheze ori refuze înrolarea unei noi telecomenzi, caz în care societatea nu poate fi ținută răspunzătoare pentru imposibilitatea utilizării acesteia.",
        "Cheile care nu sunt prezente, se vor șterge din memoria mașinii."
      ],
    },
    {
      title: "Lamelă / cheie rezidențială uzată/ruptă/non-OEM",
      items: [
        "Posibilitatea ca în cazul utilizării unei lamele / chei rezidențiale uzate, deteriorate, rupte, deformate sau neoriginale (non-OEM), funcționarea corespunzătoare atât a produsului final, cât și a copiei realizate să fie afectată. În aceste situații, pot apărea dificultăți de utilizare, funcționare parțială, uzură prematură sau imposibilitatea asigurării unei compatibilități și funcționalități de 100%.",
      ],
    },
    {
      title: "Prelucrare cheie/lamelă furnizată de client / Schimbare carcasă auto cu piesa furnizată de client",
      items: [
        "Societatea nu poate fi ținută răspunzătoare pentru eventualele probleme, neconformități sau disfuncționalități ale produsului ori ale copiei executate, generate de starea necorespunzătoare sau de calitatea redusă a lamelei / cheii furnizate de client.",
        "Clientul declară că produsul furnizat pentru prelucrare (cheie, lamelă, carcasa auto sau alte componente similare) îi aparține și își asumă responsabilitatea privind proveniența, conformitatea și compatibilitatea acestuia.",
        "Societatea nu poate garanta funcționarea corespunzătoare a produselor furnizate de client și nu își asumă răspunderea pentru eventualele nefuncționalități, incompatibilități sau defecte rezultate din calitatea, uzura, construcția sau lipsa certificărilor/conformităților produsului prezentat de client.",
      ],
    },
  ];

  const defaultFormFields = {
    logoUrl: defaultLogo,

    userLocation: null,
    telefonMagazin: "",
    email: "",
    adresaPctLucru: "",
    employer: companyInfo?.name || "",
    operator: `${firstName} ${lastName}`.trim(),
    data: getNowLocalDate(),
    ora: getNowLocalTimeHHMM(),
    client: "",
    telefon: "",
    clientEmail: "",

    produsReceptionat: null,
    produsAltulText: "",
    marca: "",
    model: "",
    anFabricatie: "",
    stareProdusLaReceptie: [],
    stareAltulText: "",
    operatiuneSolicitata: [],
    operatiuneAltulText: "",
    estimareDurataValue: "",
    estimareDurataUnit: DURATA_UNITS[0],
    pretEstimat: "",
    riscuriObservatii: "",
    showRiscuriObservatii: false,
    acknowledged: false,

    clientSignature: null,
    employeeSignature: null,
    clientConfirmSignature: null,
    clientConfirmDate: null,

    receipt: "",
    receiptInput: "",
    receiptDate: null,

    status: STATUS.NEW,
    createdFormId: null,
    publicLink: null,
    loading: true,
    errorMsg: null,

    savingReceipt: false,
    receiptMsg: null,
    receiptError: null,
  };

  const [state, setState] = useState({ ...defaultFormFields });

  const effectiveId = props.documentId || state.createdFormId;
  const isNew = !effectiveId;

  const hasAltulProdus = state.produsReceptionat?.value === "altul";
  const hasAltulStare = (state.stareProdusLaReceptie || []).some((o) => o.value === "altul");
  const hasAltulOperatiune = (state.operatiuneSolicitata || []).some((o) => o.value === "altul");

  const userLocationOptions = useMemo(() => {
    return (userLocations || []).map((l) => ({
      value: String(l.id),
      label: l.name || `Locație ${l.id}`,
      email: l.email || "",
      phone: l.phone || "",
      address: l.address || "",
      employer: l.employer || "",
      raw: l,
    }));
  }, [userLocations]);

  const produsOptions = useMemo(
    () => [
      { value: "cheie_auto_cu_telecomanda", label: "Cheie auto cu telecomandă" },
      { value: "cheie_auto_valet", label: "Cheie auto valet" },
      { value: "telecomanda_rezidentiala_cod_fix", label: "Telecomandă rezidențială Cod fix" },
      { value: "telecomanda_rezidentiala_procedura_oem", label: "Telecomandă rezidențială Procedură OEM" },
      { value: "telecomanda_rezidentiala_procedura_als", label: "Telecomandă rezidențială Procedură ALS" },
      { value: "cheie_rezidentiala_plana", label: "Cheie rezidențială plană" },
      { value: "cheie_rezidentiala_amprenta", label: "Cheie rezidențială amprentă" },
      { value: "cheie_rezidentiala_fluture", label: "Cheie rezidențială fluture" },
      { value: "cheie_rezidentiala_speciala", label: "Cheie rezidențială specială" },
      { value: "altul", label: "Altul" },
    ],
    []
  );

  const stareOptions = useMemo(
    () => [
      { value: "fara_defecte_vizibile", label: "Fără defecte vizibile" },
      { value: "probleme_transponder", label: "Probleme la transponder" },
      { value: "probleme_functia_telecomanda", label: "Probleme la funcția de telecomandă" },
      { value: "carcasa_deteriorata", label: "Carcasă deteriorată" },
      { value: "buton_defect", label: "Buton defect" },
      { value: "lamela_uzata_rupta", label: "Lamelă/ cheie rezidențială uzată/ruptă/non-OEM" },
      { value: "altul", label: "Altul" },
    ],
    []
  );

  const operatiuneOptions = useMemo(
    () => [
      { value: "copiere_cheie_auto_cu_telecomanda", label: "Copiere cheie auto cu telecomandă" },
      { value: "copiere_cheie_cu_transponder", label: "Copiere cheie cu transponder" },
      { value: "schimbare_carcasa_auto", label: "Schimbare carcasă auto" },
      { value: "mutare_transponder_cip", label: "Mutare transponder (cip)" },
      { value: "schimbare_baterie_telecomanda", label: "Schimbare baterie telecomandă" },
      { value: "prelucrare_lamela", label: "Prelucrare lamelă" },
      { value: "lipire_butoane_telecomanda", label: "Lipire butoane telecomandă" },
      { value: "reparatii_circuit_electronic", label: "Reparații circuit electronic" },
      { value: "prelucrare_cheie_lamela_furnizata_client", label: "Prelucrare cheie/lamelă furnizată de client" },
      { value: "schimbare_carcasa_auto_piesa_furnizata_client", label: "Schimbare carcasă auto cu piesa furnizată de client" },
      { value: "altul", label: "Altul" },
    ],
    []
  );

  const selectStylesGreenChecks = useMemo(() => makeSelectStylesGreenChecks(selectStyles), []);

  const normalizedStatus = String(state.status || "").toUpperCase();
  const isFinalized = normalizedStatus === "SIGNED" || normalizedStatus === "CONFIRMED";
  const isConfirmed = normalizedStatus === "CONFIRMED";
  const isReadonly = isFinalized;

  const hasSingleUserLocation = userLocationOptions.length === 1;
  const canGenerate = !isReadonly && state.status === STATUS.NEW;

  useWaitForClientSignature({
    formId: state.createdFormId,
    status: state.status,
    isFinalized,
    enabled: !isConfirmed && normalizedStatus === "PENDING",
    getClientFormById: getClientFormByIdHandler,
    shouldStop: (cf) => {
      const st = String(cf?.status || "").toUpperCase();
      return st === "SIGNED" || st === "CONFIRMED";
    },
    onSigned: async () => {
      await getPageContentWithLocations(userLocations);
    },
  });

  useWaitForClientSignature({
    formId: state.createdFormId,
    status: state.status,
    isFinalized,
    enabled: !isConfirmed && normalizedStatus === "SIGNED" && !state.clientConfirmSignature,
    getClientFormById: getClientFormByIdHandler,
    shouldStop: (cf) => !!cf?.clientConfirmSignature,
    onSigned: async () => {
      await getPageContentWithLocations(userLocations);
    },
  });


  

  const applyUserLocationToShopFields = (opt) => {
    if (!opt) return;
    if (isReadonly) return;

    setState((s) => ({
      ...s,
      userLocation: opt,
      adresaPctLucru: opt.address || opt.label || "",
      telefonMagazin: opt.phone || "",
      email: opt.email || "",
      employer: opt.employer || companyInfo?.name || "",
      errorMsg: null,
    }));
  };

  const resolveFormIdFromCompanyInfo = () => {
    const list = companyInfo?.availableClientForms ?? [];
    return list.find((id) => id?.startsWith?.(FORM_ID_PREFIX)) ?? null;
  };

  const { resolveDefaultLocationFromClockIn } = useDefaultLocationFromClockIn({
  companyId,
  employeeId,
  getClockInOutHandler,
});

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setState((s) => ({ ...s, loading: true, errorMsg: null }));

      const locResp = await getUserLocationsHandler();
      const locItems = locResp?.data?.items || [];
      const safeLocs = Array.isArray(locItems) ? locItems : [];

      if (cancelled) return;

      setUserLocations(safeLocs);
      await getPageContentWithLocations(safeLocs);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [props.documentId]); 

  const mapClientFormToState = (cf, locationsFromApi) => {
    const optionsNow = buildSelectOptionsFromLocations(locationsFromApi || userLocations);

    const cfId = cf?.id || null;
    const cfStatus = String(cf?.status || STATUS.PENDING).toUpperCase();
    const signedStatus = cfStatus === STATUS.SIGNED;

    const fields = cf?.fields || {};
    const link = cfId ? buildClientSignatureLink(cfId, "BON_COMANDA") : null;

    const savedLocationId = String(cf?.location?.id || getField(fields, "locationId", "") || "");
    const savedLocationOpt = optionsNow.find((o) => o.value === String(savedLocationId)) || optionsNow[0] || null;

    const produsKey = getField(fields, FIELD_KEYS.produsReceptionat, null);
    const produsSel = produsKey ? produsOptions.find((p) => p.value === produsKey) || null : null;

    const stareVals = getField(fields, FIELD_KEYS.stareProdusLaReceptie, []);
    const stareSel = (Array.isArray(stareVals) ? stareVals : [])
      .map((v) => stareOptions.find((o) => o.value === v))
      .filter(Boolean);

    const operVals = getField(fields, FIELD_KEYS.operatiuneSolicitata, []);
    const operSel = (Array.isArray(operVals) ? operVals : [])
      .map((v) => operatiuneOptions.find((o) => o.value === v))
      .filter(Boolean);

    const unitVal = getField(fields, FIELD_KEYS.estimareDurataUnit, DURATA_UNITS[0].value);
    const unitSel = DURATA_UNITS.find((u) => u.value === unitVal) || DURATA_UNITS[0];

    const riscuri = String(getField(fields, FIELD_KEYS.riscuriObservatii, "") || "").trim();

    const telefonMagazin = String(
      cf?.location?.phone || savedLocationOpt?.phone || getField(fields, FIELD_KEYS.telefonMagazin, "") || ""
    );
    const email = String(cf?.location?.email || savedLocationOpt?.email || getField(fields, FIELD_KEYS.email, "") || "");
    const adresaPctLucru = String(
      cf?.location?.address ||
        savedLocationOpt?.address ||
        getField(fields, FIELD_KEYS.adresaPctLucru, "") ||
        savedLocationOpt?.label ||
        ""
    );

    const acknowledged = signedStatus ? true : Boolean(getField(fields, FIELD_KEYS.acknowledged, false));
    const clientSignature = cf?.clientSignature || getField(fields, FIELD_KEYS.clientSignature, null) || null;
    const employeeSignature = cf?.employeeSignature || null;
    const clientConfirmSignature = cf?.clientConfirmSignature || null;
    const clientConfirmDate = cf?.clientConfirmDate || null;
    const receipt = String(cf?.receipt || "").trim();
    const receiptDate = cf?.receiptDate || null;

    const picture = resolvePictureUrl(companyInfo?.pictureUrl || companyInfo?.picture);
    const logoUrl = picture || defaultLogo;

    return {
      status: cfStatus,
      createdFormId: cfId,
      publicLink: link,

      logoUrl,
      userLocation: savedLocationOpt,
      telefonMagazin,
      email,
      adresaPctLucru,
      operator: String(getField(fields, FIELD_KEYS.operator, "") || ""),
      employer: String(cf?.location?.employer || companyInfo?.name || ""),
      data: String(getField(fields, FIELD_KEYS.data, "") || ""),
      ora: String(getField(fields, FIELD_KEYS.ora, "") || ""),
      client: String(getField(fields, FIELD_KEYS.client, "") || ""),
      telefon: String(getField(fields, FIELD_KEYS.telefon, "") || ""),

      produsReceptionat: produsSel,
      produsAltulText: String(getField(fields, FIELD_KEYS.produsAltulText, "") || ""),
      marca: String(getField(fields, FIELD_KEYS.marca, "") || ""),
      model: String(getField(fields, FIELD_KEYS.model, "") || ""),
      anFabricatie: String(getField(fields, FIELD_KEYS.anFabricatie, "") || ""),
      stareProdusLaReceptie: stareSel,
      stareAltulText: String(getField(fields, FIELD_KEYS.stareAltulText, "") || ""),
      operatiuneSolicitata: operSel,
      operatiuneAltulText: String(getField(fields, FIELD_KEYS.operatiuneAltulText, "") || ""),
      estimareDurataValue: String(getField(fields, FIELD_KEYS.estimareDurataValue, "") || ""),
      estimareDurataUnit: unitSel,
      pretEstimat: String(getField(fields, FIELD_KEYS.pretEstimat, "") || ""),
      riscuriObservatii: riscuri,
      showRiscuriObservatii: riscuri.length > 0,

      acknowledged,
      clientSignature,
      employeeSignature,

      clientEmail: String(cf?.clientEmail || ""),

      clientConfirmSignature,
      clientConfirmDate,
      receipt,
      receiptInput: receipt,
      receiptDate,

      receiptMsg: null,
      receiptError: null,
      savingReceipt: false,
    };
  };

  const getPageContentWithLocations = async (userLocationsParam) => {
    console.log("getPageContentWithLocations", userLocationsParam)
    console.log("isNew", isNew)
    const optionsNow = buildSelectOptionsFromLocations(userLocationsParam);
    
    if (isNew) {
      let initialLoc = (optionsNow || [])[0] || null;
      try {
        console.log("getPageContentWithLocations call resolveDefaultLocationFromClockIn with ", optionsNow)
        const clockedOpt = await resolveDefaultLocationFromClockIn(optionsNow);
        if (clockedOpt) initialLoc = clockedOpt;
      } catch(err) {
        console.error("resolveDefaultLocationFromClockIn", err);
        // ignore and fallback
      }

      const rawInitialLoc = (userLocationsParam || []).find(l => String(l.id) === initialLoc?.value);
      setState(() => ({
        ...defaultFormFields,
        userLocation: initialLoc,
        adresaPctLucru: initialLoc?.address || initialLoc?.label || "",
        telefonMagazin: initialLoc?.phone || (companyInfo?.phone || companyInfo?.telefon || ""),
        email: initialLoc?.email || companyInfo?.email || "",
        employer: rawInitialLoc?.employer || companyInfo?.name || "",
        loading: false,
        errorMsg: null,
      }));
      return;
    }

    // EDIT / PREVIEW
    const idToLoad = effectiveId;
    const resp = await getClientFormByIdHandler(idToLoad);
    const cf = resp?.data?.clientForm;

    setState(() => ({
      ...defaultFormFields,
      ...mapClientFormToState(cf, userLocationsParam),
      loading: false,
      errorMsg: null,
    }));
  };

  const buildClientFormPayload = () => {
    const fields = {};
    setFieldObj(fields, FIELD_KEYS.operator, state.operator || "");
    setFieldObj(fields, FIELD_KEYS.data, state.data || "");
    setFieldObj(fields, FIELD_KEYS.ora, state.ora || "");
    setFieldObj(fields, FIELD_KEYS.client, state.client || "");
    setFieldObj(fields, FIELD_KEYS.telefon, state.telefon || "");
    setFieldObj(fields, FIELD_KEYS.produsReceptionat, state.produsReceptionat?.value ?? "");
    setFieldObj(fields, FIELD_KEYS.produsAltulText, hasAltulProdus ? state.produsAltulText : "");
    setFieldObj(fields, FIELD_KEYS.marca, state.marca || "");
    setFieldObj(fields, FIELD_KEYS.model, state.model || "");
    setFieldObj(fields, FIELD_KEYS.anFabricatie, state.anFabricatie || "");
    setFieldObj(fields, FIELD_KEYS.stareProdusLaReceptie, (state.stareProdusLaReceptie || []).map((o) => o.value));
    setFieldObj(fields, FIELD_KEYS.stareAltulText, hasAltulStare ? state.stareAltulText : "");
    setFieldObj(fields, FIELD_KEYS.operatiuneSolicitata, (state.operatiuneSolicitata || []).map((o) => o.value));
    setFieldObj(fields, FIELD_KEYS.operatiuneAltulText, hasAltulOperatiune ? state.operatiuneAltulText : "");
    setFieldObj(fields, FIELD_KEYS.estimareDurataValue, state.estimareDurataValue || "");
    setFieldObj(fields, FIELD_KEYS.estimareDurataUnit, state.estimareDurataUnit?.value ?? "minute");
    setFieldObj(fields, FIELD_KEYS.pretEstimat, state.pretEstimat || "");
    setFieldObj(fields, FIELD_KEYS.riscuriObservatii, (state.riscuriObservatii || "").trim());
    setFieldObj(fields, FIELD_KEYS.acknowledged, Boolean(state.acknowledged));

    const locationId = state.userLocation?.value || null;
    const formId = resolveFormIdFromCompanyInfo();

    const clientName = state.client || "";
    const clientPhone = state.telefon || "";
    const clientEmail = state.clientEmail || "";

    return {
      id: isNew ? undefined : props.documentId,
      employeeId,
      locationId,
      formId,
      companyId,
      fields,
      clientName,
      clientPhone,
      clientEmail,
    };
  };

  const validate = () => {
    const errors = [];
    const isEmpty = (v) => String(v ?? "").trim().length === 0;

    // if (isEmpty(state.client)) errors.push("Client");
    // if (isEmpty(state.telefon)) errors.push("Telefon");
    if (isEmpty(state.operator)) errors.push("Operator");
    if (isEmpty(state.data)) errors.push("Data");
    if (isEmpty(state.ora)) errors.push("Ora");

    if (!state.userLocation?.value) errors.push("Locație");
    if (isEmpty(state.adresaPctLucru)) errors.push("Punct. de lucru");
    if (isEmpty(state.telefonMagazin)) errors.push("Telefon magazin");
    if (isEmpty(state.email)) errors.push("Email");

    if (!state.produsReceptionat?.value) errors.push("Produs recepționat");
    if (isEmpty(state.marca)) errors.push("Marca");
    if (isEmpty(state.model)) errors.push("Model");
    if (isEmpty(state.anFabricatie)) errors.push("An fabricație");
    if (!Array.isArray(state.stareProdusLaReceptie) || state.stareProdusLaReceptie.length === 0)
      errors.push("Stare produs la recepție");
    if (!Array.isArray(state.operatiuneSolicitata) || state.operatiuneSolicitata.length === 0)
      errors.push("Operațiune solicitată");

    const hasAltulProdusNow = state.produsReceptionat?.value === "altul";
    const hasAltulStareNow = (state.stareProdusLaReceptie || []).some((o) => o.value === "altul");
    const hasAltulOperatiuneNow = (state.operatiuneSolicitata || []).some((o) => o.value === "altul");

    if (hasAltulProdusNow && isEmpty(state.produsAltulText)) errors.push("Altul (Produs recepționat)");
    if (hasAltulStareNow && isEmpty(state.stareAltulText)) errors.push("Altul (Stare produs)");
    if (hasAltulOperatiuneNow && isEmpty(state.operatiuneAltulText)) errors.push("Altul (Operațiune)");

    if (isEmpty(state.estimareDurataValue)) errors.push("Estimare durată");
    if (!state.estimareDurataUnit?.value) errors.push("Unitate estimare durată");
    if (isEmpty(state.pretEstimat)) errors.push("Preț în lei (include TVA)");

    const employeeOk = !!employeeId;
    const locationId = state.userLocation?.value || null;
    const formId = resolveFormIdFromCompanyInfo();

    if (!employeeOk || !locationId || !formId) {
      errors.push("Config (employeeId / locationId / formId)");
    }

    if (errors.length > 0) {
      setState((s) => ({
        ...s,
        errorMsg: `Toate câmpurile sunt obligatorii. Lipsesc: ${errors.join(", ")}.`,
      }));
      return false;
    }

    setState((s) => ({ ...s, errorMsg: null }));
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = buildClientFormPayload();
    const resp = isNew ? await createClientFormHandler(payload) : await editClientFormByIdHandler(effectiveId, payload);

    if (resp.data?.error != null) {
      setState((s) => ({ ...s, errorMsg: resp.data?.message || "Error" }));
      return;
    }

    if (isNew) {
      setState((s) => ({
        ...s,
        ...defaultFormFields,
        ...mapClientFormToState(resp.data, userLocations),
        loading: false,
        errorMsg: null,
      }));
      return;
    }

    props.closeRightPanel(true);
  };

  const handlePrintPdf = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    try {
      const docData = {
        ...state,
        RISKS_GROUPS,
        clientSignature: state.clientSignature,
        employeeSignature: state.employeeSignature,
        clientConfirmSignature: state.clientConfirmSignature,
        data: formatRoDateTime(state.data, { withTime: false }),
        clientConfirmDate: formatRoDateTime(state.clientConfirmDate, { withTime: true }),
        receipt: state.receipt,
        receiptDate: formatRoDateTime(state.receiptDate, { withTime: true }),
      };
      const blob = await pdf(<ClientFormBonDeComandaPdfTemplate data={docData} companyInfo={companyInfo} />).toBlob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error("handlePrintPdf", err);
      setState((s) => ({ ...s, errorMsg: "Nu am putut genera PDF-ul pentru deschidere." }));
    }
  };

  const handleSaveReceipt = async () => {
    if (!effectiveId) return;

    const receipt = String(state.receiptInput || "").trim();
    if (!receipt) {
      setState((s) => ({
        ...s,
        receiptError: "Introdu numărul bonului.",
        receiptMsg: null,
        errorMsg: null,
      }));
      return;
    }

    setState((s) => ({
      ...s,
      savingReceipt: true,
      receiptMsg: null,
      receiptError: null,
      errorMsg: null,
    }));

    try {
      const payloadBase = buildClientFormPayload();

      const payload = {
        id: effectiveId,
        locationId: payloadBase.locationId,
        fields: payloadBase.fields,
        clientName: payloadBase.clientName,
        clientPhone: payloadBase.clientPhone,
        clientEmail: payloadBase.clientEmail,
        receipt,

        // keep these so server doesn't wipe them
        clientSignature: state.clientSignature || null,
        employeeSignature: state.employeeSignature || null,
        clientConfirmSignature: state.clientConfirmSignature || null,
        clientConfirmDate: state.clientConfirmDate || null,
      };

      const resp = await editClientFormByIdHandler(effectiveId, payload);

      if (resp?.data?.error != null) {
        setState((s) => ({
          ...s,
          receiptMsg: null,
          receiptError: resp.data?.message || "Nu am putut salva numărul bonului.",
          savingReceipt: false,
        }));
        return;
      }

      const updated = resp?.data?.clientForm ? resp.data.clientForm : resp?.data;

      setState((s) => ({
        ...s,
        receipt: String(updated?.receipt ?? receipt),
        receiptInput: String(updated?.receipt ?? receipt),
        receiptDate: updated?.receiptDate || s.receiptDate,
        receiptMsg: "Numărul bonului a fost salvat.",
        receiptError: null,
        savingReceipt: false,
      }));
    } catch (err) {
      console.error("handleSaveReceipt", err);
      setState((s) => ({
        ...s,
        savingReceipt: false,
        receiptMsg: null,
        receiptError: "Nu am putut salva numărul bonului.",
      }));
    }
  };

  if (state.loading) return <Spinner />;

  const confirmDone = !!state.clientConfirmSignature && !!state.clientConfirmDate;
  const needsConfirmReceipt = isFinalized && !state.clientConfirmSignature;
  const showQr = !!state.createdFormId && !!state.publicLink && (state.status === STATUS.PENDING || needsConfirmReceipt);

  return (
    <section className="form-container">
      <div className="details-form fisa-service-details">
        <div className="details-form fisa-service-details">
          <div className="form-group service-form-group no-bg ">
            <div className="row fisa-header-row">
              <div className="form-group service-form-group left-aligned no-bg mb-0">
                <div className="row">
                  <div className="logo-upload" role="button" tabIndex={0}>
                    <img className="logo-img" src={state.logoUrl || defaultLogo} alt="logo" />
                  </div>
                </div>
              </div>

              <div className="form-group service-form-group right-aligned no-bg">
                <div className="form-group-title-row">
                  <IconBadge>
                    <IconPhone />
                  </IconBadge>
                  <div className="row form-group-title">Date magazin</div>
                </div>

                <div className="field-row">
                  <FieldLabel className="w-150" icon={IconMapPin}>
                    Punct de lucru
                  </FieldLabel>

                  {hasSingleUserLocation ? (
                    <div className="input" style={{ display: "flex", alignItems: "center" }}>
                      {userLocationOptions[0]?.label || "-"}
                    </div>
                  ) : (
                    <Select
                      className="select"
                      options={userLocationOptions}
                      value={state.userLocation}
                      onChange={(opt) => applyUserLocationToShopFields(opt)}
                      isDisabled={isReadonly}
                      styles={selectStylesGreenChecks}
                      components={{
                        ...greenCheckComponents,
                        IndicatorSeparator: () => null,
                      }}
                    />
                  )}
                </div>

                <div className="field-row">
                  <FieldLabel className="w-150" icon={IconPhone}>
                    Telefon magazin
                  </FieldLabel>
                  <input className="input" value={state.telefonMagazin} readOnly placeholder="Telefon magazin" autoComplete="off" />
                </div>

                <div className="field-row">
                  <FieldLabel className="w-150" icon={IconMail}>
                    Email
                  </FieldLabel>
                  <input className="input" value={state.email} readOnly placeholder="Email" autoComplete="off" />
                </div>
              </div>
            </div>
          </div>

          <div className="form-title-and-subtitle centered">
            <div className="row title-row centered">
              <h1 className="title">BON DE COMANDĂ</h1>
            </div>
            <p className="subtitle">Fișă service</p>
            {state.status !== STATUS.NEW && <StatusBadge status={state.status} />}

            {isReadonly ? (
              <button type="button" onClick={handlePrintPdf} className="btn btn-success" style={{ marginTop: 10 }}>
                Printează
              </button>
            ) : null}
          </div>

          {/* DATE COMANDA */}
          <div className="form-group service-form-group">
            <div className="form-group-title-row">
              <IconBadge>
                <IconClipboardList />
              </IconBadge>
              <div className="row form-group-title">Date Comandă</div>
            </div>

            <div className="field-row">
              <FieldLabel icon={IconUser}>Operator</FieldLabel>
              <input className="input" value={state.operator} readOnly />
            </div>

            <div className="field-row">
              <FieldLabel icon={IconUser}>Angajator</FieldLabel>
              <input className="input" value={state.employer || ""} readOnly />
            </div>

            <div className="field-row two">
              <FieldLabel icon={IconCalendar}>Data</FieldLabel>
              <input
                type="date"
                className="input"
                value={state.data}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, data: e.target.value, errorMsg: null }))}
              />

              <FieldLabel className="no-width" icon={IconClock}>
                Ora
              </FieldLabel>

              {isReadonly ? (
                <input className="input" value={state.ora} readOnly />
              ) : (
                <TimeInput24h className="input" value={state.ora} onChange={(val) => setState((s) => ({ ...s, ora: val }))} />
              )}
            </div>

            <div className="form-divider" />

            <div className="field-row two">
              <FieldLabel icon={IconUser}>Client (opțional)</FieldLabel>
              <input
                className="input"
                value={state.client}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, client: e.target.value, errorMsg: null }))}
              />

              <FieldLabel className="no-width" icon={IconPhone}>
                Telefon (opțional)
              </FieldLabel>
              <input
                className="input"
                value={state.telefon}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, telefon: e.target.value, errorMsg: null }))}
              />
            </div>
          </div>

          {/* Produs recepționat */}
          <div className="form-group service-form-group">
            <div className="form-group-title-row">
              <IconBadge>
                <IconPackage />
              </IconBadge>
              <div className="row form-group-title">Produs recepționat</div>
            </div>

            <div className="row">
              <Select
                className="select"
                options={produsOptions}
                isDisabled={isReadonly}
                onChange={(opt) => setState((s) => ({ ...s, produsReceptionat: opt, errorMsg: null }))}
                value={state.produsReceptionat}
                styles={selectStylesGreenChecks}
                components={{
                  ...greenCheckComponents,
                  IndicatorSeparator: () => null,
                }}
              />
            </div>

            {hasAltulProdus ? (
              <div className="field-row">
                <FieldLabel>Altul</FieldLabel>
                <input
                  className="input"
                  placeholder="Introdu produsul recepționat"
                  value={state.produsAltulText}
                  readOnly={isReadonly}
                  onChange={(e) => setState((s) => ({ ...s, produsAltulText: e.target.value, errorMsg: null }))}
                />
              </div>
            ) : null}

            <div className="field-row two">
              <FieldLabel icon={IconPackage}>Marca</FieldLabel>
              <input
                className="input"
                placeholder="Introdu X dacă nu face parte din gama auto"
                value={state.marca}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, marca: e.target.value, errorMsg: null }))}
              />

              <FieldLabel className="no-width" icon={IconPackage}>Model</FieldLabel>
              <input
                className="input"
                placeholder="Introdu X dacă nu face parte din gama auto"
                value={state.model}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, model: e.target.value, errorMsg: null }))}
              />
            </div>

            <div className="field-row">
              <FieldLabel icon={IconCalendar}>An fabricație</FieldLabel>
              <input
                className="input"
                placeholder="Introdu X dacă nu face parte din gama auto"
                value={state.anFabricatie}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, anFabricatie: e.target.value, errorMsg: null }))}
              />
            </div>
            <p className="form-info-text">
              Câmpurile sunt obligatorii pentru tot ce înseamnă operațiune auto (Telecomenzi, Carcase, Baterii, etc.)
            </p>
          </div>

          {/* Stare produs */}
          <div className="form-group service-form-group">
            <div className="form-group-title-row">
              <IconBadge>
                <IconFileText />
              </IconBadge>
              <div className="row form-group-title">Stare produs la recepție</div>
            </div>

            <div className="row">
              <Select
                className="select"
                isMulti
                hideSelectedOptions={false}
                isDisabled={isReadonly}
                options={stareOptions}
                onChange={(opts) => setState((s) => ({ ...s, stareProdusLaReceptie: opts || [], errorMsg: null }))}
                value={state.stareProdusLaReceptie}
                styles={selectStylesGreenChecks}
                components={greenCheckComponents}
              />
            </div>

            {hasAltulStare ? (
              <div className="field-row">
                <FieldLabel className="no-width">Altul</FieldLabel>
                <input
                  className="input"
                  placeholder="Introdu starea produsului"
                  value={state.stareAltulText}
                  readOnly={isReadonly}
                  onChange={(e) => setState((s) => ({ ...s, stareAltulText: e.target.value, errorMsg: null }))}
                />
              </div>
            ) : null}
          </div>

          {/* Operațiune */}
          <div className="form-group service-form-group">
            <div className="form-group-title-row">
              <IconBadge>
                <IconWrench />
              </IconBadge>
              <div className="row form-group-title">Operațiune solicitată</div>
            </div>

            <div className="row">
              <Select
                className="select"
                isMulti
                hideSelectedOptions={false}
                isDisabled={isReadonly}
                options={operatiuneOptions}
                value={state.operatiuneSolicitata}
                onChange={(opts) => setState((s) => ({ ...s, operatiuneSolicitata: opts || [], errorMsg: null }))}
                styles={selectStylesGreenChecks}
                components={greenCheckComponents}
              />
            </div>

            {hasAltulOperatiune ? (
              <div className="field-row">
                <FieldLabel className="no-width">Altul</FieldLabel>
                <input
                  placeholder="Introdu operațiunea solicitată"
                  className="input"
                  value={state.operatiuneAltulText}
                  readOnly={isReadonly}
                  onChange={(e) => setState((s) => ({ ...s, operatiuneAltulText: e.target.value, errorMsg: null }))}
                />
              </div>
            ) : null}
          </div>

          {/* Estimare și preț */}
          <div className="form-group service-form-group">
            <div className="form-group-title-row">
              <IconBadge>
                <IconBanknote />
              </IconBadge>
              <div className="row form-group-title">Estimare și preț</div>
            </div>

            <div className="field-row two">
              <FieldLabel className="w-150" icon={IconTimer}>
                Estimare durată
              </FieldLabel>
              <input
                className="input"
                value={state.estimareDurataValue}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, estimareDurataValue: e.target.value, errorMsg: null }))}
              />

              <Select
                className="select"
                options={DURATA_UNITS}
                isDisabled={isReadonly}
                value={state.estimareDurataUnit}
                onChange={(opt) => setState((s) => ({ ...s, estimareDurataUnit: opt, errorMsg: null }))}
                styles={selectStylesGreenChecks}
                components={greenCheckComponents}
              />
            </div>

            <div className="field-row">
              <FieldLabel className="w-150" icon={IconBanknote}>
                Preț în lei (include TVA)
              </FieldLabel>
              <input
                className="input"
                value={state.pretEstimat}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, pretEstimat: e.target.value, errorMsg: null }))}
              />
            </div>
          </div>

          {/* Riscuri */}
          <div className="form-group service-form-group warning">
            <div className="form-group-title-row">
              <IconBadge>
                <IconTriangleAlert />
              </IconBadge>
              <div className="row form-group-title">Riscuri specifice intervenției, aduse la cunoștință și acceptate de client</div>
            </div>

            <div
              className="input"
              style={{
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
                cursor: "default",
              }}
            >
              {RISKS_GROUPS.map((group, gi) => (
                <div key={gi} style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4}}>{group.title}</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {group.items.map((line, idx) => (
                      <li key={idx} style={{ marginBottom: 4 }}>
                        {line}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {!state.showRiscuriObservatii && !isReadonly ? (
              <div className="field-row" style={{ marginTop: 10 }}>
                  <a
                    className="link"
                    onClick={(e) => {
                      e.preventDefault();
                      setState((s) => ({ ...s, showRiscuriObservatii: true, errorMsg: null }));
                    }}
                    style={{ userSelect: "none" }}
                  >
                    Adaugă&nbsp;observații
                  </a>
                </div>) : null}
            {state.showRiscuriObservatii ? (
              <div className="field-row" style={{ marginTop: 10 }}>
                <FieldLabel>Observații</FieldLabel>
                <input
                  className="input"
                  placeholder="Scrie observațiile aici..."
                  value={state.riscuriObservatii}
                  readOnly={isReadonly}
                  onChange={(e) => setState((s) => ({ ...s, riscuriObservatii: e.target.value, errorMsg: null }))}
                  autoComplete="off"
                />
                {state.showRiscuriObservatii && !isReadonly && (
                  <span
                    className="small-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      setState((s) => ({ ...s, showRiscuriObservatii: false, riscuriObservatii: null, errorMsg: null }));
                    }}
                  >
                    <svg
                      height="20"
                      width="20"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                      focusable="false"
                      className="css-tj5bde-Svg"
                    >
                      <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
                    </svg>
                  </span>
                )}
              </div>
            ) : null}
          </div>
          
          {/* Actualizare BON - show only if confirm receipt exists */}
          {confirmDone ? (
            <div className="form-group service-form-group">
              <div className="form-group-title-row">
                <IconBadge>
                  <IconClipboardList />
                </IconBadge>
                <div className="row form-group-title">Actualizare Bon</div>
              </div>

              <div className="field-row two" style={{ alignItems: "center" }}>
                <FieldLabel className="w-150" icon={IconFileText}>
                  Număr bon
                </FieldLabel>

                <input
                  className="input"
                  value={state.receiptInput}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      receiptInput: e.target.value,
                      receiptMsg: null,
                      receiptError: null,
                      errorMsg: null,
                    }))
                  }
                  placeholder="Ex: 12345"
                  autoComplete="off"
                />

                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleSaveReceipt}
                  disabled={state.savingReceipt}
                  style={{ minWidth: 170 }}
                >
                  {state.savingReceipt ? "Se salvează..." : "Salvează număr bon"}
                </button>
              </div>

              {state.receiptMsg ? (
                <div className="error success" style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>
                  {state.receiptMsg}
                </div>
              ) : null}

              {state.receiptError ? (
                <div className="error" style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>
                  {state.receiptError}
                </div>
              ) : null}

              {state.receiptDate ? (
                <div className="field-row" style={{ marginTop: 8 }}>
                  <FieldLabel className="w-150" icon={IconCalendar}>
                    Data bon
                  </FieldLabel>
                  <input
                    className="input"
                    value={formatRoDateTime(state.receiptDate, { withTime: true })}
                    readOnly
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {/* SIGNED / CONFIRMED */}
          {isFinalized ? (
            <div className="form-group service-form-group success">
              <div className="form-group-title-row">
                <IconBadge>
                  <IconFileText />
                </IconBadge>
                <div className="row form-group-title">Acord și semnături</div>
              </div>

              <div className={"agreed input"} style={{ marginTop: 10 }}>
                <div style={{ fontWeight: 600 }}>
                  <div className="row" style={{ cursor: "not-allowed" }} role="button" tabIndex={0}>
                    <div className="checkbox" style={{ cursor: "not-allowed" }}>
                      <input type="checkbox" checked readOnly />
                      <div className="checksquare"></div>
                    </div>
                    <p> Am luat la cunoștință riscurile acestei operațiuni.</p>
                  </div>
                </div>
              </div>

              <div className="field-row two" style={{ marginTop: 10 }}>
                <FieldLabel icon={IconUser}>Subsemnatul:</FieldLabel>
                <input className="input" value={state.client || ""} readOnly />

                <FieldLabel icon={IconUser}>Angajatul:</FieldLabel>
                <input className="input" value={state.operator || ""} readOnly />
              </div>

              <div className="field-row two">
                <FieldLabel icon={IconCalendar}>Data:</FieldLabel>
                <input className="input" type="date" value={state.data || ""} readOnly />

                <FieldLabel icon={IconCalendar}>Data:</FieldLabel>
                <input className="input" type="date" value={state.data || ""} readOnly />
              </div>

              <div className="field-row two" style={{ alignItems: "flex-start" }}>
                <FieldLabel icon={IconFileText}>Semnătura client:</FieldLabel>
                <div className="input dotted signature-input" style={{ padding: 10, minHeight: 120, overflow: "hidden" }}>
                  {state.clientSignature ? (
                    <img
                      src={state.clientSignature}
                      alt="Semnătura client"
                      style={{ maxWidth: "100%", maxHeight: 160, display: "block" }}
                    />
                  ) : (
                    <div style={{ opacity: 0.6 }}>—</div>
                  )}
                </div>
              </div>            
            </div>
          ) : null}

          {/* QR: show if PENDING OR SIGNED but needs confirm receipt */}
          {showQr ? (
            <div className="form-group service-form-group">
              <div className="form-group-title-row">
                <div className="row form-group-title">
                  {state.status === STATUS.PENDING ? "Semnare client (QR)" : "Confirmare recepție produs (QR)"}
                </div>
              </div>

              <div className="row" style={{ alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <div>
                  <QRCodeCanvas value={state.publicLink} size={180} includeMargin />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontWeight: 600 }}>
                    {state.status === STATUS.PENDING
                      ? "Scanează cu telefonul pentru a semna bonul de comandă"
                      : "Scanează cu telefonul pentru confirmarea recepției produsului"}
                  </div>

                  <a className="link ml-0" href={state.publicLink} target="_blank" rel="noreferrer">
                    Deschide declarația în tab nou
                  </a>

                  <div style={{ fontSize: 12, opacity: 0.75 }}>ID: {state.createdFormId}</div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="row buttons-container">
            <button onClick={() => props.closeRightPanel(true)} className="btn btn-default">
              {isReadonly || state.createdFormId ? "Închide" : "Anulează"}
            </button>

            {!isReadonly && canGenerate && !state.createdFormId ? (
              <button onClick={handleSubmit} className="btn btn-success">
                Generează
              </button>
            ) : null}
          </div>

          {state.errorMsg ? <div className="error">&#9888;&nbsp;{state.errorMsg}</div> : null}
        </div>
      </div>
    </section>
  );
}

export default ClientFormBonDeComandaDetails;